import Map, {
  FullscreenControl, MapLayerMouseEvent,
  MapRef,
  ScaleControl,
  ViewStateChangeEvent,
} from 'react-map-gl';
import 'mapbox-gl/dist/mapbox-gl.css';
import { ReactNode, useCallback, useEffect, useRef } from 'react';
import {
  selectCoordinates,
  setCoordinates,
  setZoomLevel,
} from 'pages/itinerary/MapSlice';
import useAppDispatch from 'core/hooks/useAppDispatch';
import useAppSelector from 'core/hooks/useAppSelector';
import useCountryContext, {
  CountryContext,
} from 'core/hooks/useCountryContext';

export interface MapBoxMapProps {
  id: string;
  zoomLevel: number;
  children?: ReactNode;
  interactiveLayerIds: string[] | undefined;
  onMouseMove?: (event: MapLayerMouseEvent) => void;
}

const MapBoxMap = ({ id, children, zoomLevel, interactiveLayerIds, onMouseMove }: MapBoxMapProps) => {
  const mapRef = useRef<MapRef>(null);
  const isDarkMode = localStorage.theme === 'true' ?? true;
  const mapStyle = isDarkMode
    ? 'mapbox://styles/mapbox/dark-v10'
    : 'mapbox://styles/mapbox/light-v10';
  const dispatch = useAppDispatch();
  const viewCoordinates = useAppSelector(selectCoordinates);
  const { countryContext } = useCountryContext();

  useEffect(() => {
    if (countryContext === CountryContext.RO) {
      dispatch(
        setCoordinates({
          latitude: 45.9442858,
          longitude: 25.0094303,
        }),
      );
      dispatch(setZoomLevel(6));
    }

    if (countryContext === CountryContext.HU) {
      dispatch(
        setCoordinates({
          latitude: 47.1575376,
          longitude: 19.4835623,
        }),
      );
      dispatch(setZoomLevel(7));
    }

    if (countryContext === CountryContext.BG) {
      dispatch(
        setCoordinates({
          latitude: 42.766111,
          longitude: 25.238333,
        }),
      );
      dispatch(setZoomLevel(7));
    }
  }, [countryContext, dispatch]);

  const onMapLoad = useCallback(() => {
    mapRef.current?.loadImage('/arrow_deliveries.png', (err, image) => {
      if (err) {
        return;
      }
      mapRef.current?.addImage('arrowDeliveries', image!);
    });

    mapRef.current?.loadImage('/arrow_stops.png', (err, image) => {
      if (err) {
        return;
      }
      mapRef.current?.addImage('arrowStops', image!);
    });
    mapRef.current?.loadImage('/arrow_courier.png', (err, image) => {
      if (err) {
        return;
      }
      mapRef.current?.addImage('arrowCourier', image!);
    });
  }, []);

  const onMove = useCallback(
    ({ viewState }: ViewStateChangeEvent) => {
      dispatch(
        setCoordinates({
          latitude: viewState.latitude,
          longitude: viewState.longitude,
        }),
      );
      dispatch(setZoomLevel(viewState.zoom));
    },
    [dispatch],
  );

  return (
    <Map
      id={id}
      ref={mapRef}
      style={{ width: '100%', height: '100%' }}
      mapStyle={mapStyle}
      {...viewCoordinates}
      zoom={zoomLevel}
      onLoad={onMapLoad}
      onRender={(event) => event.target.resize()}
      onMove={onMove}
      onMouseMove={onMouseMove}
      interactiveLayerIds={interactiveLayerIds}
    >
      <FullscreenControl position="top-left" />
      <ScaleControl position={'bottom-left'} />
      {children}
    </Map>
  );
};

MapBoxMap.defaultProps = {
  children: null,
};

export default MapBoxMap;
